import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Typography, Divider } from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/ext-language_tools';

interface EmailEditorProps {
  onChange: (value: string) => void;
  initialValue?: string;
}

interface EmailMetadata {
  subject: string;
  preheader: string;
}

const defaultTemplate = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta name="color-scheme" content="light dark">
  <title>Welcome to Mailzen</title>
  <meta name="description" content="Stay updated with our latest newsletter">
</head>
<body style="margin: 0; padding: 0; background-color: #f8faff; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
    <tr>
      <td align="center" style="padding: 20px 10px;">
        <table class="mobile-full-width" width="600" cellpadding="0" cellspacing="0" border="0" role="presentation" style="max-width: 600px; width: 100%; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);">
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #2A2F6F 0%, #0F1642 100%); padding: 40px 20px; text-align: center;" class="mobile-padding">
              <h1 style="color: white; margin: 0 0 10px 0; font-size: 28px; font-weight: bold; font-family: Arial, sans-serif;">Welcome to Our Newsletter</h1>
              <p style="color: #E0E7FF; margin: 0; font-size: 16px; font-family: Arial, sans-serif;">Stay updated with our latest news and insights</p>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 40px 20px;" class="mobile-padding">
              <h2 style="color: #2A2F6F; margin: 0 0 20px 0; font-size: 24px; font-family: Arial, sans-serif;">Hello there! 👋</h2>
              
              <p style="color: #4B5563; margin: 0 0 30px 0; line-height: 1.6; font-family: Arial, sans-serif;">
                We're excited to have you on board! Here's what you can expect from our newsletter:
              </p>

              <!-- Features List -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td style="padding: 20px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation">
                      <tr>
                        <td width="24" style="padding-right: 15px;">
                          <div style="width: 24px; height: 24px; background-color: #6366F1; border-radius: 50%;"></div>
                        </td>
                        <td style="color: #2A2F6F; font-weight: 500; font-family: Arial, sans-serif;">
                          Weekly industry insights
                        </td>
                      </tr>
                      <tr><td height="15"></td></tr>
                      <tr>
                        <td width="24" style="padding-right: 15px;">
                          <div style="width: 24px; height: 24px; background-color: #8B5CF6; border-radius: 50%;"></div>
                        </td>
                        <td style="color: #2A2F6F; font-weight: 500; font-family: Arial, sans-serif;">
                          Expert tips and tricks
                        </td>
                      </tr>
                      <tr><td height="15"></td></tr>
                      <tr>
                        <td width="24" style="padding-right: 15px;">
                          <div style="width: 24px; height: 24px; background-color: #EC4899; border-radius: 50%;"></div>
                        </td>
                        <td style="color: #2A2F6F; font-weight: 500; font-family: Arial, sans-serif;">
                          Exclusive member content
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

              <p style="color: #4B5563; margin: 0 0 30px 0; line-height: 1.6; font-family: Arial, sans-serif;">
                We'll be sending you valuable content every week. Feel free to reply to any of our emails with your thoughts or questions.
              </p>

              <!-- CTA Button -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center">
                    <a href="#" style="display: inline-block; background: linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%); color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; font-family: Arial, sans-serif;">Get Started</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #F8FAFF; padding: 20px; text-align: center; border-top: 1px solid #E5E7EB; border-radius: 0 0 8px 8px;">
              <p style="color: #6B7280; margin: 0 0 10px 0; font-size: 14px; font-family: Arial, sans-serif;">
                © 2024 Your Company. All rights reserved.
              </p>
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td align="center">
                    <a href="#" style="color: #6366F1; text-decoration: none; margin: 0 10px; font-family: Arial, sans-serif; font-size: 14px;">Unsubscribe</a>
                    <a href="#" style="color: #6366F1; text-decoration: none; margin: 0 10px; font-family: Arial, sans-serif; font-size: 14px;">Privacy Policy</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

export const EmailEditor: React.FC<EmailEditorProps> = ({ onChange, initialValue = defaultTemplate }) => {
  const [value, setValue] = useState(initialValue);
  const [metadata, setMetadata] = useState<EmailMetadata>({
    subject: '',
    preheader: ''
  });

  useEffect(() => {
    // Insert subject and preheader into the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');
    
    // Update title with subject
    const titleElement = doc.querySelector('title');
    if (titleElement) {
      titleElement.textContent = metadata.subject || 'Email Template';
    }

    // Update or insert preheader meta tag
    let preheaderMeta = doc.querySelector('meta[name="description"]');
    if (!preheaderMeta) {
      preheaderMeta = doc.createElement('meta');
      preheaderMeta.setAttribute('name', 'description');
      doc.head.appendChild(preheaderMeta);
    }
    preheaderMeta.setAttribute('content', metadata.preheader);

    // Add hidden preheader text for email clients
    let preheaderDiv = doc.querySelector('.preheader') as HTMLDivElement;
    if (!preheaderDiv) {
      preheaderDiv = doc.createElement('div');
      preheaderDiv.className = 'preheader';
      preheaderDiv.style.cssText = 'display: none !important; visibility: hidden; mso-hide: all; font-size: 1px; line-height: 1px; max-height: 0; max-width: 0; opacity: 0; overflow: hidden;';
      doc.body.insertBefore(preheaderDiv, doc.body.firstChild);
    }
    preheaderDiv.textContent = metadata.preheader;

    const updatedHtml = doc.documentElement.outerHTML;
    onChange(updatedHtml);
  }, [value, metadata, onChange]);

  const handleEditorChange = (newValue: string) => {
    setValue(newValue);
    onChange(newValue);
  };

  const handleMetadataChange = (field: keyof EmailMetadata) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setMetadata(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Paper elevation={0} sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      borderRadius: 2
    }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Email Metadata
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Subject Line"
            value={metadata.subject}
            onChange={handleMetadataChange('subject')}
            placeholder="Enter your email subject"
          />
          <TextField
            fullWidth
            size="small"
            label="Preheader Text"
            value={metadata.preheader}
            onChange={handleMetadataChange('preheader')}
            placeholder="Preview text shown in inbox"
          />
        </Box>
      </Box>
      <Box sx={{ 
        flex: 1,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <AceEditor
          mode="html"
          theme="tomorrow"
          onChange={handleEditorChange}
          value={value}
          name="email-editor"
          width="100%"
          height="100%"
          editorProps={{ $blockScrolling: true }}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            showLineNumbers: true,
            tabSize: 2,
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }}
        />
      </Box>
    </Paper>
  );
}; 